import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, formatINR, formatArea } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import {
  Plus, Pencil, Trash2, CheckCircle2, RotateCcw, Eye, ExternalLink, X,
  LayoutDashboard, Home as HomeIcon, PlusSquare, Users, ShieldCheck, CreditCard, MessageSquare,
  Upload, FileText, Award, TrendingUp, Sparkles, Heart, ClipboardList
} from "lucide-react";

const SELLER_PLANS = [
  { title: "Free", price: "₹0", period: "Current plan", features: ["Unlimited listings", "Aggregate lead insights", "Basic profile", "Email support"], active: true },
  { title: "Boost Status", price: "Coming Soon", period: "Per property boost", features: ["Push property above regular listings", "Similar to Amazon paid boost", "Priority in search results", "7 / 30 day boosts"], disabled: true },
  { title: "Featured Seller", price: "Coming Soon", period: "Monthly membership", features: ["Featured across Tharlo", "Premium seller badge", "Priority lead routing", "Dedicated Tharlo advisor"], disabled: true },
];

const completenessColor = (score) => {
  if (score >= 75) return "text-gold";
  if (score >= 50) return "text-ink";
  return "text-muted-foreground";
};

const SIDEBAR = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "saved", label: "Saved Properties", icon: Heart },
  { key: "requirements", label: "My Requirements", icon: ClipboardList },
  { key: "properties", label: "My Listings", icon: HomeIcon },
  { key: "add", label: "Add New Property", icon: PlusSquare },
  { key: "leads", label: "Seller Leads", icon: Users },
  { key: "verification", label: "Profile Verification", icon: ShieldCheck },
  { key: "subscription", label: "Subscription Plans", icon: CreditCard },
  { key: "support", label: "Support", icon: MessageSquare },
];

export default function BrokerDashboard() {
  const { user, loading, openRequirement } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeSection = params.get("s") || "dashboard";
  const setSection = (k) => { const np = new URLSearchParams(params); np.set("s", k); setParams(np); };

  if (loading) return <div className="container-tharlo py-20">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  // Buyer-only users should not be here — send them to buyer dashboard
  if (user.user_type === "buyer") return <Navigate to="/dashboard" replace />;

  const isSellerOnly = user.user_type === "seller";
  const sidebarItems = SIDEBAR.filter((s) => {
    if (isSellerOnly && (s.key === "saved" || s.key === "requirements")) return false;
    return true;
  });

  return (
    <div className="container-tharlo py-8" data-testid="broker-dashboard">
      <SEO title="Seller Dashboard" description="Manage your listings, leads, verification and profile on Tharlo." noindex />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="bg-white border border-border p-5 sticky top-24">
            <div className="pb-4 border-b border-border">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Signed in as</div>
              <div className="font-display text-lg text-ink mt-1 truncate">{user.name}</div>
              {user.role === "admin" && <div className="text-[10px] uppercase tracking-widest text-gold mt-1">Admin</div>}
              {!isSellerOnly && user.user_type === "both" && (
                <div className="text-[10px] uppercase tracking-widest text-gold mt-1">Buyer + Seller</div>
              )}
            </div>
            <nav className="mt-4 space-y-1">
              {sidebarItems.map((s) => {
                const Icon = s.icon;
                const active = activeSection === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => (s.key === "add" ? window.location.assign("/sell") : setSection(s.key))}
                    data-testid={`sidebar-${s.key}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors ${active ? "bg-ink text-white" : "text-foreground/80 hover:bg-muted"}`}
                  >
                    <Icon className="w-4 h-4" /> {s.label}
                  </button>
                );
              })}
            </nav>
            {user.user_type === "both" && (
              <Link to="/dashboard" data-testid="seller-goto-buyer" className="mt-4 block text-xs text-gold hover:underline text-center">
                Switch to Buyer Dashboard →
              </Link>
            )}
          </div>
        </aside>

        <main className="lg:col-span-9">
          {activeSection === "dashboard" && <DashboardHome onNav={setSection} />}
          {!isSellerOnly && activeSection === "saved" && <SavedProperties />}
          {!isSellerOnly && activeSection === "requirements" && <MyRequirements openPost={openRequirement} />}
          {activeSection === "properties" && <MyPropertiesList />}
          {activeSection === "leads" && <LeadsSection />}
          {activeSection === "verification" && <VerificationCenter />}
          {activeSection === "subscription" && <SubscriptionPlans />}
          {activeSection === "support" && <SupportSection />}
        </main>
      </div>
    </div>
  );
}

/* ============ Dashboard Home (metrics + profile) ============ */
function DashboardHome({ onNav }) {
  const [metrics, setMetrics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);

  const refresh = async () => {
    const [m, p] = await Promise.all([api.get("/me/metrics"), api.get("/me/profile")]);
    setMetrics(m.data); setProfile(p.data);
  };
  useEffect(() => { refresh(); }, []);

  if (!metrics || !profile) return <div className="text-sm text-muted-foreground">Loading dashboard...</div>;

  const tierColor = { Basic: "bg-slate-200 text-slate-700", Active: "bg-blue-100 text-blue-700", Trusted: "bg-gold text-white", Premium: "bg-ink text-gold" };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Seller Dashboard</div>
        <h1 className="font-display text-4xl text-ink tracking-tight">Welcome, {profile.name}</h1>
      </div>

      {/* Profile Completion */}
      <div className="bg-white border border-border p-6" data-testid="profile-completion-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Award className="w-5 h-5 text-gold" />
              <span className="text-sm font-semibold text-ink">Profile Completion</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${tierColor[profile.profile.tier]}`}>{profile.profile.tier} Seller</span>
            </div>
            <div className="w-full h-2 bg-muted overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${profile.profile.score}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{profile.profile.score}% complete</span>
              <span className="text-muted-foreground">Complete profile for better visibility & trust</span>
            </div>
          </div>
          <button onClick={() => setEditing(true)} data-testid="edit-profile-btn" className="btn-outline !py-2.5">
            <Pencil className="w-4 h-4" /> Edit Profile
          </button>
        </div>
      </div>

      {/* Metrics grid */}
      <div>
        <h3 className="font-display text-xl text-ink mb-4">Performance Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Metric label="Active Listings" value={metrics.active_listings} icon={<HomeIcon className="w-4 h-4" />} accent />
          <Metric label="Premium Listings" value={metrics.premium_listings} icon={<Sparkles className="w-4 h-4" />} />
          <Metric label="Verified Listings" value={metrics.verified_listings} icon={<ShieldCheck className="w-4 h-4" />} />
          <Metric label="Total Views" value={metrics.total_views} icon={<Eye className="w-4 h-4" />} />
          <Metric label="Buyer Leads" value={metrics.buyer_leads} icon={<Users className="w-4 h-4" />} accent />
          <Metric label="Deals Closed" value={metrics.deals_closed} icon={<CheckCircle2 className="w-4 h-4" />} />
          <Metric label="Contact Reveals" value="—" small="Coming soon" />
          <Metric label="Site Visits" value="—" small="Coming soon" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ActionCard title="Add New Property" text="List your next property in under 3 minutes" cta="Add Listing" onClick={() => window.location.assign("/sell")} />
        <ActionCard title="Verify Your Profile" text="Upload RERA, GST, ID docs to boost credibility" cta="Go to Verification" onClick={() => onNav("verification")} />
      </div>

      {editing && <EditProfileModal profile={profile} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
    </div>
  );
}

function Metric({ label, value, icon, accent, small }) {
  return (
    <div className={`p-4 border ${accent ? "bg-gold/10 border-gold/30" : "bg-white border-border"}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        {icon && <span className="text-gold">{icon}</span>}
      </div>
      <div className="font-display text-2xl text-ink mt-2">{value}</div>
      {small && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{small}</div>}
    </div>
  );
}

function ActionCard({ title, text, cta, onClick }) {
  return (
    <div className="bg-ink text-white p-6 flex items-start gap-4">
      <div className="flex-1">
        <h4 className="font-display text-lg">{title}</h4>
        <p className="text-white/70 text-sm mt-1">{text}</p>
        <button onClick={onClick} className="mt-3 text-gold text-sm font-semibold hover:underline">{cta} →</button>
      </div>
    </div>
  );
}

/* ============ My Properties ============ */
function MyPropertiesList() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    const r = await api.get("/my/properties");
    setItems(r.data.items);
  };
  useEffect(() => { refresh(); }, []);

  const toggleSold = async (p) => {
    try { await api.patch(`/my/properties/${p.id}`, { is_sold: !p.is_sold }); toast.success(p.is_sold ? "Marked as Available" : "Marked as Sold Out"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/my/properties/${p.id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display text-3xl text-ink tracking-tight">My Properties ({items.length})</h2>
        <Link to="/sell" data-testid="add-listing-btn" className="btn-gold !py-2.5"><Plus className="w-4 h-4" /> New</Link>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <h3 className="font-display text-xl text-ink mb-2">No listings yet</h3>
          <Link to="/sell" className="btn-primary mt-4">Submit first property</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-border p-4 flex flex-col lg:flex-row gap-4" data-testid={`my-prop-${p.id}`}>
              <img src={p.images?.[0] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200"} alt="" className="w-full lg:w-40 aspect-[4/3] object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <StatusBadge status={p.status} isSold={p.is_sold} />
                  <span className="trust-badge">{p.property_type}</span>
                  {p.verified && <span className="trust-badge bg-gold text-white border-gold">Verified</span>}
                </div>
                <h4 className="font-display text-lg text-ink truncate">{p.title}</h4>
                <div className="text-sm text-muted-foreground">{p.locality}, {p.city}</div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <span><span className="text-muted-foreground">Price: </span><strong className="text-ink">{formatINR(p.price)}</strong></span>
                  <span><span className="text-muted-foreground">Area: </span><strong className="text-ink">{formatArea(p.area_value, p.area_unit)}</strong></span>
                  <span className="flex items-center gap-1 text-muted-foreground"><Eye className="w-3 h-3" />{p.views || 0} views</span>
                  {typeof p.completeness_score === "number" && (
                    <span className={`flex items-center gap-1 ${completenessColor(p.completeness_score)}`}>
                      <span className="text-xs">Completeness:</span> <strong>{p.completeness_score}%</strong>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 lg:flex-col lg:min-w-[150px]">
                <Link to={`/property/${p.slug || p.id}`} className="text-xs px-3 py-2 border border-border hover:border-ink flex items-center gap-1.5 justify-center"><ExternalLink className="w-3 h-3" /> View</Link>
                <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="text-xs px-3 py-2 bg-ink text-white flex items-center gap-1.5 justify-center"><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => toggleSold(p)} data-testid={`sold-${p.id}`} className={`text-xs px-3 py-2 flex items-center gap-1.5 justify-center ${p.is_sold ? "border border-border" : "bg-gold text-white"}`}>
                  {p.is_sold ? <><RotateCcw className="w-3 h-3" />Available</> : <><CheckCircle2 className="w-3 h-3" />Mark Sold</>}
                </button>
                <button onClick={() => remove(p)} data-testid={`delete-${p.id}`} className="text-xs px-3 py-2 border border-red-300 text-red-600 flex items-center gap-1.5 justify-center"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditPropertyModal property={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function StatusBadge({ status, isSold }) {
  if (isSold) return <span className="trust-badge bg-red-100 text-red-700 border-red-300">Sold Out</span>;
  if (status === "approved") return <span className="trust-badge bg-green-100 text-green-700 border-green-300">Live</span>;
  if (status === "pending") return <span className="trust-badge bg-yellow-100 text-yellow-700 border-yellow-300">Pending</span>;
  return <span className="trust-badge">{status}</span>;
}

/* ============ Leads (aggregate only) ============ */
function LeadsSection() {
  const [metrics, setMetrics] = useState(null);
  useEffect(() => { api.get("/me/metrics").then((r) => setMetrics(r.data)); }, []);
  if (!metrics) return <div>Loading...</div>;
  return (
    <div className="space-y-6" data-testid="leads-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Leads Overview</h2>
        <p className="text-sm text-muted-foreground mt-2">
          For buyer privacy and platform trust, individual contact details stay with Tharlo advisors who qualify and forward serious buyers to you directly.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total Buyer Leads" value={metrics.buyer_leads} icon={<Users className="w-4 h-4" />} accent />
        <Metric label="Total Views" value={metrics.total_views} icon={<Eye className="w-4 h-4" />} />
        <Metric label="Contact Reveals" value="—" small="Coming soon" />
        <Metric label="Site Visits Scheduled" value="—" small="Coming soon" />
      </div>
      <div className="bg-white border border-border p-6">
        <h3 className="font-display text-xl text-ink mb-3">How Tharlo handles your leads</h3>
        <ol className="space-y-3 text-sm text-foreground/80">
          <li className="flex gap-3"><span className="w-6 h-6 bg-ink text-gold flex items-center justify-center shrink-0 text-xs font-bold">1</span> Buyer expresses interest in your listing via the property page</li>
          <li className="flex gap-3"><span className="w-6 h-6 bg-ink text-gold flex items-center justify-center shrink-0 text-xs font-bold">2</span> Tharlo qualifies the lead (budget, timeline, seriousness)</li>
          <li className="flex gap-3"><span className="w-6 h-6 bg-ink text-gold flex items-center justify-center shrink-0 text-xs font-bold">3</span> Qualified leads are shared with you via call/WhatsApp from Tharlo</li>
          <li className="flex gap-3"><span className="w-6 h-6 bg-ink text-gold flex items-center justify-center shrink-0 text-xs font-bold">4</span> Aggregate counts (views, leads) are shown here for transparency</li>
        </ol>
      </div>
    </div>
  );
}

/* ============ Profile Verification ============ */
function VerificationCenter() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState("");

  const refresh = async () => {
    const r = await api.get("/me/docs"); setDocs(r.data.items);
  };
  useEffect(() => { refresh(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    // Client-side validation (backend also validates)
    const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
    const nameOk = /\.(pdf|jpe?g|png)$/i.test(file.name || "");
    if (!ALLOWED.includes(file.type) && !nameOk) {
      toast.error("Only PDF, JPG or PNG files are allowed");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB");
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
      await api.post("/me/docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — Tharlo admin will review shortly");
      refresh();
    } catch (err) {
      logDev.error("doc upload failed:", err);
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    }
    finally { setUploading(""); }
  };

  const KINDS = [
    { key: "govt_id", label: "Government ID Proof", desc: "Aadhaar / PAN / Passport (internal, admin-only)" },
    { key: "rera_cert", label: "RERA Certificate", desc: "Adds trust badge to your profile" },
    { key: "gst_cert", label: "GST Registration", desc: "For brokers / developers" },
    { key: "business_reg", label: "Business Registration Certificate", desc: "For agencies & developers" },
  ];

  // Map raw DB status to a buyer-friendly label + colour
  const statusLabel = (s) => {
    if (s === "approved") return { text: "Verified", cls: "text-green-700 bg-green-50 border-green-200" };
    if (s === "rejected") return { text: "Rejected — please re-upload", cls: "text-red-700 bg-red-50 border-red-200" };
    // "pending" from backend → clearer "Uploaded" state for the user
    return { text: "Uploaded — under review", cls: "text-gold bg-gold/10 border-gold/40" };
  };

  return (
    <div className="space-y-6" data-testid="verification-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Profile Verification Center</h2>
        <p className="text-sm text-muted-foreground mt-2">Upload PDF or photo (JPG/PNG). All uploaded documents are visible <strong>only to Tharlo administrators</strong> and used for verification. Not shown publicly.</p>
      </div>

      <div className="space-y-3">
        {KINDS.map((k) => {
          const existing = docs.find((d) => d.kind === k.key);
          const s = existing ? statusLabel(existing.status) : null;
          const isUploading = uploading === k.key;
          let uploadLabel = existing ? "Re-upload" : "Upload";
          if (isUploading) uploadLabel = "Uploading...";
          return (
            <div key={k.key} className="bg-white border border-border p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`doc-row-${k.key}`}>
              <FileText className="w-8 h-8 text-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink">{k.label}</div>
                <div className="text-xs text-muted-foreground">{k.desc}</div>
                {existing && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest px-2 py-1 border ${s.cls}`} data-testid={`doc-status-${k.key}`}>
                      <CheckCircle2 className="w-3 h-3" /> {s.text}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Uploaded on {existing.created_at?.slice(0, 10)}
                    </span>
                  </div>
                )}
              </div>
              <label
                className={`${existing ? "btn-outline" : "btn-primary"} !py-2.5 cursor-pointer whitespace-nowrap`}
                data-testid={`upload-${k.key}`}
              >
                {existing ? <RotateCcw className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                {uploadLabel}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  className="hidden"
                  disabled={isUploading}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(k.key, f); }}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className="bg-gold/10 border border-gold/30 p-5 text-sm">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-gold" /> <strong>Coming Soon</strong></div>
        Paid property-level &ldquo;Tharlo Verified&rdquo; status for each listing — comprehensive on-ground verification by our team.
      </div>
    </div>
  );
}

/* ============ Subscription Plans ============ */
function SubscriptionPlans() {
  return (
    <div className="space-y-6" data-testid="subscription-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Subscription Plans</h2>
        <p className="text-sm text-muted-foreground mt-2">Currently enjoying a free trial. Paid boosts and featured seller status launching soon.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SELLER_PLANS.map((plan) => <PlanCard key={plan.title} {...plan} />)}
      </div>
    </div>
  );
}

function PlanCard({ title, price, period, features, active, disabled }) {
  return (
    <div className={`p-6 border ${active ? "bg-ink text-white border-ink" : "bg-white border-border"} ${disabled ? "opacity-70" : ""}`}>
      <div className="font-display text-2xl">{title}</div>
      <div className={`text-3xl font-bold mt-2 ${active ? "text-gold" : "text-ink"}`}>{price}</div>
      <div className={`text-xs uppercase tracking-widest mt-1 ${active ? "text-white/60" : "text-muted-foreground"}`}>{period}</div>
      <ul className={`mt-5 space-y-2 text-sm ${active ? "text-white/85" : "text-foreground/80"}`}>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2"><CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-gold" : "text-gold"}`} /> {f}</li>
        ))}
      </ul>
      {active && <div className="mt-5 text-xs uppercase tracking-widest text-gold">Active</div>}
      {disabled && <div className="mt-5 text-xs uppercase tracking-widest text-muted-foreground">Notify me when live</div>}
    </div>
  );
}

/* ============ Support ============ */
function SupportSection() {
  const [form, setForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [lastTag, setLastTag] = useState(null);
  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error("Subject & message required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/support", form);
      const tag = r?.data?.tag;
      setLastTag(tag || null);
      toast.success(tag ? `Ticket ${tag} created. We'll respond within 24 hours.` : "Ticket sent to Tharlo.");
      setForm({ subject: "", message: "" });
    } catch (err) {
      logDev.error("support submit failed:", err);
      toast.error("Failed to submit ticket. Please try again.");
    }
    finally { setBusy(false); }
  };
  const copyTag = () => {
    if (!lastTag) return;
    navigator.clipboard?.writeText(lastTag).then(
      () => toast.success("Ticket ID copied"),
      () => toast.error("Could not copy — please note the ID manually"),
    );
  };
  return (
    <div className="space-y-6" data-testid="support-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Support</h2>
        <p className="text-sm text-muted-foreground mt-2">Have a question or need help? Send us a message. Or email <a href="mailto:hello@tharlo.com" className="text-gold hover:underline">hello@tharlo.com</a> directly.</p>
      </div>
      {lastTag && (
        <div className="bg-green-50 border border-green-300 p-5 space-y-2" data-testid="support-tag-block">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">Ticket received — Tharlo will get back to you within 24 hours.</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-green-800/80">Your ticket ID</span>
            <code className="text-base font-mono font-bold text-ink bg-white border border-green-300 px-3 py-1" data-testid="support-tag">{lastTag}</code>
            <button type="button" onClick={copyTag} data-testid="support-tag-copy" className="text-xs text-gold hover:underline font-semibold">Copy</button>
          </div>
          <p className="text-xs text-green-800/80">Please quote this ID in any email or WhatsApp reply so we can pull up your ticket instantly.</p>
        </div>
      )}
      <form onSubmit={submit} className="bg-white border border-border p-6 space-y-3 max-w-2xl" data-testid="support-form">
        <input data-testid="support-subject" required className={input} placeholder="Subject" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} />
        <textarea data-testid="support-message" required className={input} rows={6} placeholder="How can we help?" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} />
        <button data-testid="support-submit" disabled={busy} className="btn-gold w-full">{busy ? "Sending..." : "Send Ticket"}</button>
      </form>
    </div>
  );
}

/* ============ Saved / Liked Properties ============ */
function SavedProperties() {
  const [items, setItems] = useState(null);

  const refresh = async () => {
    const r = await api.get("/my/likes");
    setItems(r.data.items);
  };
  useEffect(() => { refresh(); }, []);

  if (items === null) return <div className="text-sm text-muted-foreground">Loading saved properties...</div>;

  return (
    <div className="space-y-6" data-testid="saved-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Saved Properties</h2>
        <p className="text-sm text-muted-foreground mt-2">Properties you've hearted. Tap the heart on any card to remove.</p>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <Heart className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-display text-xl text-ink mb-2">No saved properties yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Tap the heart icon on any property card to save it here.</p>
          <Link to="/properties" className="btn-primary">Browse Properties</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <PropertyCard
              key={p.id}
              p={p}
              index={i}
              liked={true}
              onLikeChange={(_id, liked) => { if (!liked) refresh(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ My Requirements (buyer requirements) ============ */
function MyRequirements({ openPost }) {
  const [items, setItems] = useState(null);

  const refresh = async () => {
    const r = await api.get("/my/requirements");
    setItems(r.data.items);
  };
  useEffect(() => { refresh(); }, []);

  if (items === null) return <div className="text-sm text-muted-foreground">Loading requirements...</div>;

  const fmtBudget = (min, max) => {
    if (!min && !max) return "—";
    if (min && max) return `${formatINR(min)} – ${formatINR(max)}`;
    if (min) return `${formatINR(min)}+`;
    return `up to ${formatINR(max)}`;
  };

  return (
    <div className="space-y-6" data-testid="requirements-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl text-ink tracking-tight">My Requirements</h2>
          <p className="text-sm text-muted-foreground mt-2">Requirements you've shared with Tharlo. Our advisors match and reach out.</p>
        </div>
        <button onClick={openPost} data-testid="post-requirement-dashboard" className="btn-gold !py-2.5">
          <Sparkles className="w-4 h-4" /> Post New Requirement
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <ClipboardList className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-display text-xl text-ink mb-2">No requirements posted yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Tell Tharlo what you're looking for and we'll match properties to you.</p>
          <button onClick={openPost} className="btn-primary">Post Your First Requirement</button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-border p-5" data-testid={`req-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="trust-badge bg-ink text-white border-ink">{r.property_type}</span>
                {r.listing_type !== "any" && <span className="trust-badge">{r.listing_type}</span>}
                <span className={`trust-badge ${r.status === "new" ? "bg-yellow-100 text-yellow-700 border-yellow-300" : "bg-green-100 text-green-700 border-green-300"}`}>{r.status}</span>
                <span className="text-xs text-muted-foreground ml-auto">{r.created_at?.slice(0, 10)}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground">Location: </span><strong>{r.locality ? `${r.locality}, ` : ""}{r.city}</strong></div>
                <div><span className="text-muted-foreground">Budget: </span><strong>{fmtBudget(r.budget_min, r.budget_max)}</strong></div>
                <div><span className="text-muted-foreground">Timeline: </span><strong>{r.timeline || "—"}</strong></div>
              </div>
              {r.bhk && <div className="mt-2 text-sm"><span className="text-muted-foreground">BHK: </span><strong>{r.bhk}</strong></div>}
              {r.notes && <div className="mt-3 text-sm text-foreground/80 border-l-2 border-gold pl-3 bg-muted/30 p-3">{r.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Modals ============ */
function EditPropertyModal({ property, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: property.title, description: property.description, price: property.price,
    area_value: property.area_value, area_unit: property.area_unit,
    property_type: property.property_type, listing_type: property.listing_type,
    locality: property.locality, address: property.address || "",
    bedrooms: property.bedrooms || 0, bathrooms: property.bathrooms || 0,
    road_width_ft: property.road_width_ft || "", legal_status: property.legal_status || "",
    video_url: property.video_url || "", map_link: property.map_link || "",
    images: property.images || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error(`${f.name}: only JPG/PNG/WebP allowed`); continue; }
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      }
      if (uploaded.length) {
        setForm((s) => ({ ...s, images: [...s.images, ...uploaded] }));
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} added`);
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
  };

  const handleVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"].includes(f.type)) {
      toast.error("Only MP4/MOV/WebM allowed"); return;
    }
    if (f.size > 100 * 1024 * 1024) { toast.error("Max 100MB video"); return; }
    setUploadingVideo(true); setVideoProgress(0);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/upload/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setVideoProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setForm((s) => ({ ...s, video_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
      toast.success("Video uploaded");
    } catch {
      toast.error("Video upload failed");
    } finally {
      setUploadingVideo(false); setVideoProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.images.filter(Boolean).length < 1) { toast.error("Keep at least 1 image on the listing"); return; }
    setSaving(true);
    try {
      await api.patch(`/my/properties/${property.id}`, {
        ...form,
        images: form.images.filter(Boolean),
        price: parseFloat(form.price), area_value: parseFloat(form.area_value),
        bedrooms: parseInt(form.bedrooms || 0), bathrooms: parseInt(form.bathrooms || 0),
        road_width_ft: form.road_width_ft ? parseFloat(form.road_width_ft) : null,
      });
      toast.success("Updated"); onSaved();
    } catch { toast.error("Update failed"); setSaving(false); }
  };
  let videoUploadLabel = form.video_url ? "Replace with new video" : "Upload video from device";
  if (uploadingVideo) videoUploadLabel = `Uploading... ${videoProgress}%`;

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-modal">
      <div className="bg-white w-full max-w-2xl my-8 border border-border">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h2 className="font-display text-2xl text-ink">Edit Property</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <input className={input} placeholder="Title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} required />
          <input className={input} placeholder="Locality" value={form.locality} onChange={(e)=>setForm({...form, locality:e.target.value})} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input type="number" className={input} placeholder="Price ₹" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} required />
            <input type="number" className={input} placeholder="Area" value={form.area_value} onChange={(e)=>setForm({...form, area_value:e.target.value})} required />
            <select className={input} value={form.area_unit} onChange={(e)=>setForm({...form, area_unit:e.target.value})}>
              <option value="sqft">sqft</option><option value="sqyd">sqyd</option><option value="acre">acre</option><option value="bigha">bigha</option>
            </select>
            <input type="number" className={input} placeholder="Road (ft)" value={form.road_width_ft} onChange={(e)=>setForm({...form, road_width_ft:e.target.value})} />
          </div>
          {form.property_type === "residential" && (
            <div className="grid grid-cols-2 gap-3">
              <input type="number" className={input} placeholder="Bedrooms" value={form.bedrooms} onChange={(e)=>setForm({...form, bedrooms:e.target.value})} />
              <input type="number" className={input} placeholder="Bathrooms" value={form.bathrooms} onChange={(e)=>setForm({...form, bathrooms:e.target.value})} />
            </div>
          )}
          <textarea className={input} rows={4} placeholder="Description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} required />

          {/* ===== Photos ===== */}
          <div className="border border-border p-4 space-y-3" data-testid="edit-images-section">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-ink">Photos ({form.images.length})</div>
                <div className="text-xs text-muted-foreground">JPG/PNG/WebP, max 8MB each. Hover a photo to remove it.</div>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="edit-add-images-btn"
                className="text-xs px-3 py-2 bg-ink text-white flex items-center gap-1.5 disabled:opacity-60"
              >
                <Upload className="w-3 h-3" /> {uploading ? "Uploading..." : "Add Photos"}
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" data-testid="edit-images-input" />
            </div>
            {form.images.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {form.images.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative group aspect-[4/3] border border-border overflow-hidden">
                    <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-ink/80 text-white px-1.5 py-0.5">Cover</span>}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`edit-remove-image-${i}`}
                      aria-label={`Remove photo ${i + 1}`}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-red-600">No photos — add at least 1 photo before saving.</div>
            )}
          </div>

          {/* ===== Video ===== */}
          <div className="border border-border p-4 space-y-3" data-testid="edit-video-section">
            <div className="text-sm font-semibold text-ink">Video (optional)</div>
            <input className={input} placeholder="Video URL (YouTube/Vimeo or uploaded)" value={form.video_url} onChange={(e)=>setForm({...form, video_url:e.target.value})} data-testid="edit-video-url" />
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                data-testid="edit-upload-video-btn"
                className="text-xs px-3 py-2 border border-border flex items-center gap-1.5 disabled:opacity-60"
              >
                <Upload className="w-3 h-3" /> {videoUploadLabel}
              </button>
              {form.video_url && !uploadingVideo && (
                <button
                  type="button"
                  onClick={() => setForm((s) => ({ ...s, video_url: "" }))}
                  data-testid="edit-remove-video-btn"
                  className="text-xs px-3 py-2 border border-red-200 text-red-600 flex items-center gap-1.5"
                >
                  <Trash2 className="w-3 h-3" /> Remove video
                </button>
              )}
              <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" onChange={handleVideoFile} className="hidden" data-testid="edit-video-input" />
            </div>
            {uploadingVideo && (
              <div className="w-full bg-muted h-1.5 overflow-hidden">
                <div className="bg-gold h-full transition-all" style={{ width: `${videoProgress}%` }} />
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-border py-3 text-sm">Cancel</button>
            <button type="submit" disabled={saving || uploading || uploadingVideo} className="flex-1 btn-primary">{saving ? "Saving..." : "Save Changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProfileModal({ profile, onClose, onSaved }) {
  const [changePw, setChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [f, setF] = useState({
    full_name: profile.full_name || profile.name || "",
    seller_type: profile.seller_type || "owner",
    mobile: profile.mobile || profile.phone || "",
    email: profile.email && !profile.email.endsWith("@phone.tharlo") ? profile.email : "",
    city: profile.city || "Rewa",
    state: profile.state || "Madhya Pradesh",
    pincode: profile.pincode || "",
    bio: profile.bio || "",
    office_address: profile.office_address || "",
    whatsapp: profile.whatsapp || "",
    website: profile.website || "",
    languages: (profile.languages || []).join(", "),
    agency_name: profile.agency_name || "",
    company_name: profile.company_name || "",
    experience_years: profile.experience_years || "",
    area_of_operation: profile.area_of_operation || "",
    specialization: profile.specialization || "",
    team_size: profile.team_size || "",
    rera_number: profile.rera_number || "",
    years_in_business: profile.years_in_business || "",
    projects_completed: profile.projects_completed || "",
    gst_number: profile.gst_number || "",
    preferred_contact: profile.preferred_contact || "call",
    contact_hours: profile.contact_hours || "",
    weekends_available: !!profile.weekends_available,
    video_calls_supported: !!profile.video_calls_supported,
  });
  const [saving, setSaving] = useState(false);
  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      payload.languages = f.languages ? f.languages.split(",").map(s=>s.trim()).filter(Boolean) : [];
      payload.experience_years = f.experience_years ? parseInt(f.experience_years) : null;
      payload.team_size = f.team_size ? parseInt(f.team_size) : null;
      payload.years_in_business = f.years_in_business ? parseInt(f.years_in_business) : null;
      payload.projects_completed = f.projects_completed ? parseInt(f.projects_completed) : null;
      await api.patch("/me/profile", payload);
      toast.success("Profile updated"); onSaved();
    } catch { toast.error("Update failed"); setSaving(false); }
  };
  const isBroker = f.seller_type === "broker";
  const isDev = f.seller_type === "developer";
  let pwToggleLabel = profile.has_password ? "Change Password" : "Set Password";
  if (changePw) pwToggleLabel = "Cancel password change";

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-profile-modal">
      <div className="bg-white w-full max-w-2xl my-8 border border-border">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white z-10">
          <h2 className="font-display text-2xl text-ink">Edit Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <Section title="Account">
            <select data-testid="profile-seller-type" className={input} value={f.seller_type} onChange={(e)=>setF({...f, seller_type:e.target.value})}>
              <option value="owner">Individual Owner</option>
              <option value="broker">Broker / Agent</option>
              <option value="developer">Developer / Builder</option>
            </select>
            <input data-testid="profile-full-name" className={input} placeholder="Full name" value={f.full_name} onChange={(e)=>setF({...f, full_name:e.target.value})} required />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="profile-mobile" className={input} placeholder="Mobile" value={f.mobile} onChange={(e)=>setF({...f, mobile:e.target.value})} required />
              <input className={input} placeholder="Email (optional)" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} />
            </div>
          </Section>

          {(isBroker || isDev) && (
            <Section title={isBroker ? "Broker / Agency" : "Developer / Builder"}>
              {isBroker && <input className={input} placeholder="Agency / Firm name" value={f.agency_name} onChange={(e)=>setF({...f, agency_name:e.target.value})} />}
              {isDev && <input className={input} placeholder="Company name" value={f.company_name} onChange={(e)=>setF({...f, company_name:e.target.value})} />}
              <div className="grid grid-cols-2 gap-3">
                <input type="number" className={input} placeholder={isBroker?"Years of experience":"Years in business"} value={isBroker?f.experience_years:f.years_in_business} onChange={(e)=>setF(isBroker?{...f, experience_years:e.target.value}:{...f, years_in_business:e.target.value})} />
                {isBroker && <input type="number" className={input} placeholder="Team size" value={f.team_size} onChange={(e)=>setF({...f, team_size:e.target.value})} />}
                {isDev && <input type="number" className={input} placeholder="Projects completed" value={f.projects_completed} onChange={(e)=>setF({...f, projects_completed:e.target.value})} />}
              </div>
              {isBroker && <input className={input} placeholder="Area of operation (e.g. Rewa, Bhopal, Delhi)" value={f.area_of_operation} onChange={(e)=>setF({...f, area_of_operation:e.target.value})} />}
              {isBroker && <input className={input} placeholder="Specialization (Residential/Commercial/Land)" value={f.specialization} onChange={(e)=>setF({...f, specialization:e.target.value})} />}
              <input className={input} placeholder="RERA Registration Number" value={f.rera_number} onChange={(e)=>setF({...f, rera_number:e.target.value})} />
              <input className={input} placeholder="GST Number" value={f.gst_number} onChange={(e)=>setF({...f, gst_number:e.target.value})} />
            </Section>
          )}

          <Section title="Public Profile">
            <textarea className={input} rows={3} placeholder="Short bio (100-300 chars)" value={f.bio} onChange={(e)=>setF({...f, bio:e.target.value})} />
            <input className={input} placeholder="Languages (comma-separated: Hindi, English)" value={f.languages} onChange={(e)=>setF({...f, languages:e.target.value})} />
            <input className={input} placeholder="Office address" value={f.office_address} onChange={(e)=>setF({...f, office_address:e.target.value})} />
            <div className="grid grid-cols-3 gap-3">
              <input className={input} placeholder="City *" value={f.city} onChange={(e)=>setF({...f, city:e.target.value})} required />
              <input className={input} placeholder="State *" value={f.state} onChange={(e)=>setF({...f, state:e.target.value})} required />
              <input className={input} placeholder="Pincode" value={f.pincode} onChange={(e)=>setF({...f, pincode:e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={input} placeholder="WhatsApp" value={f.whatsapp} onChange={(e)=>setF({...f, whatsapp:e.target.value})} />
              <input className={input} placeholder="Website" value={f.website} onChange={(e)=>setF({...f, website:e.target.value})} />
            </div>
          </Section>

          <Section title="Communication">
            <div className="grid grid-cols-2 gap-3">
              <select className={input} value={f.preferred_contact} onChange={(e)=>setF({...f, preferred_contact:e.target.value})}>
                <option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
              </select>
              <input className={input} placeholder="Contact hours (e.g. 10am-7pm)" value={f.contact_hours} onChange={(e)=>setF({...f, contact_hours:e.target.value})} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.weekends_available} onChange={(e)=>setF({...f, weekends_available:e.target.checked})} className="accent-gold" /> Available on weekends</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.video_calls_supported} onChange={(e)=>setF({...f, video_calls_supported:e.target.checked})} className="accent-gold" /> Supports video calls</label>
          </Section>

          <Section title="Security">
            <button type="button" onClick={() => setChangePw(!changePw)} data-testid="toggle-change-pw" className="text-sm text-gold hover:underline">
              {pwToggleLabel}
            </button>
            {changePw && (
              <div className="space-y-2 border border-border p-3 bg-muted/30">
                {profile.has_password && (
                  <input data-testid="pw-current" type="password" className={input} placeholder="Current password" value={pwForm.current} onChange={(e)=>setPwForm({...pwForm, current:e.target.value})} />
                )}
                <input data-testid="pw-next" type="password" className={input} placeholder="New password (min 8)" value={pwForm.next} onChange={(e)=>setPwForm({...pwForm, next:e.target.value})} />
                <input data-testid="pw-confirm" type="password" className={input} placeholder="Confirm new password" value={pwForm.confirm} onChange={(e)=>setPwForm({...pwForm, confirm:e.target.value})} />
                <button type="button" data-testid="pw-save" disabled={pwBusy} onClick={async () => {
                  if (pwForm.next.length < 8) { toast.error("Password must be 8+ chars"); return; }
                  if (pwForm.next !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
                  setPwBusy(true);
                  try {
                    if (profile.has_password) {
                      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
                    } else {
                      await api.post("/auth/set-password", { password: pwForm.next });
                    }
                    toast.success("Password updated");
                    setChangePw(false); setPwForm({ current: "", next: "", confirm: "" });
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  finally { setPwBusy(false); }
                }} className="btn-primary w-full">{pwBusy ? "Saving..." : "Save Password"}</button>
              </div>
            )}
          </Section>

          <div className="flex gap-2 pt-3 sticky bottom-0 bg-white pb-1">
            <button type="button" onClick={onClose} className="flex-1 border border-border py-3 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="profile-save" className="flex-1 btn-primary">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h4 className="font-display text-lg text-ink">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
